import { SvgChart } from './SvgChart';

import type { Surface, PendingAction, Slot } from '@ampshift/shared';
import { useI18n, formatDateTime } from '../i18n';

export type ConfirmationCard = Extract<Surface, { type: 'confirmation-card' }>;
type TextSurface = Extract<Surface, { type: 'text' }>;
type TableSurface = Extract<Surface, { type: 'table' }>;
type ChartSurface = Extract<Surface, { type: 'chart' }>;
type SlotPicker = Extract<Surface, { type: 'slot-picker' }>;
type Clarification = Extract<Surface, { type: 'clarification' }>;
type ErrorSurface = Extract<Surface, { type: 'error' }>;
type CalendarSurface = Extract<Surface, { type: 'calendar' }>;
type NoteSurface = Extract<Surface, { type: 'clinical-note' }>;
type SummarySurface = Extract<Surface, { type: 'patient-summary' }>;
type CdsSurface = Extract<Surface, { type: 'cds-alerts' }>;

export interface SurfaceHandlers {
  onSlotPick?: (slot: Slot) => void;
  onConfirm?: (card: ConfirmationCard) => Promise<void> | void;
  onReject?: (card: ConfirmationCard) => Promise<void> | void;
  onReply?: (text: string) => void;
}

/** Build the same confirmation-card shape the chat endpoint returns, from a pending action a direct tool call produced. */
export function cardFromPendingAction(pa: PendingAction, title: string, lines: Array<{ label: string; value: string }>): ConfirmationCard {
  return {
    type: 'confirmation-card', pendingActionId: pa.pendingActionId, kind: pa.kind, title, patient: null,
    lines: lines.length > 0 ? lines : [{ label: '', value: pa.summary }],
    cdsFindings: [], requiresAcknowledgement: pa.requiresAcknowledgement, expiresAt: pa.expiresAt, confirmLabel: 'Confirm', rejectLabel: 'Cancel',
  };
}

export function SurfaceView({ surface, handlers = {} }: { surface: Surface; handlers?: SurfaceHandlers }) {
  switch (surface.type) {
    case 'text': return <TextView s={surface} />;
    case 'table': return <TableView s={surface} />;
    case 'chart': return <ChartView s={surface} />;
    case 'slot-picker': return <SlotPickerView s={surface} onPick={handlers.onSlotPick} />;
    case 'confirmation-card': return <ConfirmationCardView s={surface} onConfirm={handlers.onConfirm} onReject={handlers.onReject} />;
    case 'clarification': return <ClarificationView s={surface} onReply={handlers.onReply} />;
    case 'error': return <ErrorView s={surface} />;
    case 'medication-list':
      return (
        <ul className="surface">
          {surface.medications.map((m) => <li key={m.id}>{m.name} {m.dose ?? ''} {m.frequency ?? ''} <span className="muted">({m.status})</span></li>)}
        </ul>
      );
    case 'calendar': return <CalendarView s={surface} />;
    case 'clinical-note': return <NoteView s={surface} />;
    case 'patient-summary': return <SummaryView s={surface} />;
    case 'cds-alerts': return <CdsView s={surface} />;
    // Header-only signal for the staff app; the portal already knows who the patient is.
    case 'patient-indicator': return null;
    case 'form':
      return <div className="surface muted">{surface.title}</div>;
    default:
      return null;
  }
}

function CalendarView({ s }: { s: CalendarSurface }) {
  const { t, lang } = useI18n();
  return (
    <div className="surface">
      <strong>{s.title}</strong>
      {s.appointments.length === 0 ? (
        <div className="muted">{t('noAppointments')}</div>
      ) : (
        <div className="stack" style={{ marginTop: 6 }}>
          {s.appointments.map((a) => (
            <div key={a.appointmentId} className="appt">
              <div className="appt-head">
                <span className="appt-when">{formatDateTime(a.start, lang, s.timeZone)}</span>
                <span className={`pill ${a.status === 'CANCELLED' || a.status === 'NO_SHOW' ? 'pill-danger' : a.status === 'CHECKED_IN' || a.status === 'COMPLETED' ? 'pill-ok' : ''}`}>{a.status.replace('_', ' ')}</span>
              </div>
              <div className="muted">{[a.providerName, a.appointmentTypeCode, a.reason].filter(Boolean).join(' · ')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteView({ s }: { s: NoteSurface }) {
  const { lang } = useI18n();
  return (
    <div className="surface">
      <strong>{s.title ?? s.status}</strong>
      {s.date ? <div className="muted">{formatDateTime(s.date, lang)}{s.author ? ` · ${s.author}` : ''}</div> : null}
      <div className="surface-text" style={{ marginTop: 6 }}>{s.body}</div>
    </div>
  );
}

function SummaryView({ s }: { s: SummarySurface }) {
  const { t } = useI18n();
  return (
    <div className="surface">
      <strong>{s.patient.name.given} {s.patient.name.family}{s.age !== undefined ? ` · ${s.age}` : ''}</strong>
      {s.sections.map((sec) => (
        <div key={sec.title} style={{ marginTop: 8 }}>
          <div className="muted" style={{ fontWeight: 600 }}>{sec.title}</div>
          {sec.items.length === 0 ? (
            <div className="muted">{sec.emptyMessage ?? '—'}</div>
          ) : (
            <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
              {sec.items.map((it, i) => (
                <li key={`${it.label}-${i}`}>{it.label}: {it.value}{it.date ? <span className="muted"> · {it.date}</span> : null}{it.flag ? <span className={`flag flag-${it.flag}`}> {it.flag}</span> : null}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
      {s.narrative ? <div className="prov" style={{ marginTop: 8 }}>{t('aiNarrative')}: {s.narrative}</div> : null}
    </div>
  );
}

function CdsView({ s }: { s: CdsSurface }) {
  return (
    <div className="surface">
      {s.findings.map((f) => (
        <div key={f.findingId} className="cds" style={f.severity === 'critical' ? { borderColor: 'var(--danger)', background: 'var(--danger-soft)' } : undefined}>
          <strong>{f.severity.toUpperCase()}</strong> · {f.summary}
          {f.detail ? <div className="muted">{f.detail}</div> : null}
          <div className="prov">{f.source.label}</div>
        </div>
      ))}
    </div>
  );
}

function TextView({ s }: { s: TextSurface }) {
  const { t } = useI18n();
  return (
    <div className="surface surface-text">
      {s.markdown}
      {s.provenance === 'ai-narrative' && <div className="prov">{t('aiNarrative')}</div>}
    </div>
  );
}

function TableView({ s }: { s: TableSurface }) {
  return (
    <div className="surface tbl-wrap">
      {s.title && <strong>{s.title}</strong>}
      {s.rows.length === 0 ? <div className="muted">{s.emptyMessage ?? '—'}</div> : (
        <table className="tbl">
          <thead><tr>{s.columns.map((c) => <th key={c.key} style={{ textAlign: c.align ?? 'start' }}>{c.label}</th>)}</tr></thead>
          <tbody>
            {s.rows.map((r, i) => (
              <tr key={i}>{s.columns.map((c) => <td key={c.key} style={{ textAlign: c.align ?? 'start' }}>{String(r[c.key] ?? '')}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ChartView({ s }: { s: ChartSurface }) {
  const series = s.series.map((ser, i) => ({
    name: ser.name,
    color: ['#0f766e', '#2a78d6', '#eb6834', '#eda100'][i % 4] ?? '#0f766e',
    points: ser.points.map((p) => ({ x: p.x, label: p.x.slice(0, 10), y: p.y, ...(p.flag ? { flag: p.flag } : {}) })),
  }));
  if (series.length === 0) return null;
  return (
    <div className="surface">
      <strong>{s.title}</strong>
      <div className="chart">
        <SvgChart kind={s.kind} series={series} title={s.title} height={220} {...(s.unit ? { unit: s.unit } : {})} {...(s.referenceRange ? { referenceRange: s.referenceRange } : {})} />
      </div>
      {s.caption && <div className="muted">{s.caption}</div>}
    </div>
  );
}

export function SlotPickerView({ s, onPick }: { s: SlotPicker; onPick?: (slot: Slot) => void }) {
  const { lang } = useI18n();
  return (
    <div className="surface">
      <strong>{s.title}</strong>
      {s.selectionPrompt && <div className="muted">{s.selectionPrompt}</div>}
      <div className="slots" style={{ marginTop: 6 }}>
        {s.slots.map((slot) => (
          <button key={`${slot.providerId}-${slot.start}`} className="slot" onClick={() => onPick?.(slot)} disabled={!onPick}>
            {formatDateTime(slot.start, lang)}{slot.providerName ? ` · ${slot.providerName}` : ''}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ConfirmationCardView({ s, onConfirm, onReject }: { s: ConfirmationCard; onConfirm?: (c: ConfirmationCard) => Promise<void> | void; onReject?: (c: ConfirmationCard) => Promise<void> | void }) {
  const { t, lang } = useI18n();
  return (
    <div className="surface conf-card" data-testid="confirmation-card">
      <h3>{s.title}</h3>
      <dl className="conf-lines">
        {s.lines.map((l, i) => (
          <div key={i} style={{ display: 'contents' }}>
            <dt>{l.label}</dt>
            <dd>{l.value}</dd>
          </div>
        ))}
      </dl>
      {s.cdsFindings.map((f) => <div key={f.findingId} className="cds"><strong>{f.severity.toUpperCase()}</strong> {f.summary}</div>)}
      <div className="muted">{t('expires')}: {formatDateTime(s.expiresAt, lang)}</div>
      <div className="conf-actions">
        <button className="btn btn-primary" onClick={() => onConfirm?.(s)} disabled={!onConfirm}>{s.confirmLabel === 'Confirm' ? t('confirm') : s.confirmLabel}</button>
        <button className="btn" onClick={() => onReject?.(s)} disabled={!onReject}>{s.rejectLabel === 'Cancel' ? t('reject') : s.rejectLabel}</button>
      </div>
    </div>
  );
}

function ClarificationView({ s, onReply }: { s: Clarification; onReply?: (text: string) => void }) {
  return (
    <div className="surface">
      <div>{s.question}</div>
      <div className="row" style={{ marginTop: 6 }}>
        {s.options.map((o) => (
          <button key={o.id} className="btn btn-sm" onClick={() => onReply?.(o.reply)} disabled={!onReply} title={o.detail}>{o.label}</button>
        ))}
      </div>
    </div>
  );
}

function ErrorView({ s }: { s: ErrorSurface }) {
  return <div className="surface err" role="alert">{s.message} <span className="muted">({s.code})</span></div>;
}
